import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import axios from "axios";
import { Tab, Tabs, Table, Button, Form, Card, Spinner, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import $ from "jquery";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Download, FileText, CreditCard, ArrowLeftRight } from "lucide-react";

import AppDatePicker from "./AppDatePicker";
import { fmtDate, fmtDateOnly, fmtTimeOnly, fmtFileDate, fmtMaybeDate } from "./Copper/copperConfig";
import { useTimezone } from "../hook/useTimezone";
import "./PortfolioDetails.css";

const PortfolioDetails = () => {
  // Subscribe to the global timezone so all date cells re-render when the
  // user picks a different zone in the header dropdown.
  useTimezone();

  const { encodedId } = useParams();
  const clientId = encodedId ? atob(encodedId) : null;
  const location = useLocation();
  const navigate = useNavigate();

  const [clientTitle, setClientTitle] = useState(location.state?.clientName || "");
  const [clientEmail, setClientEmail] = useState(location.state?.clientEmail || "");

  // Transactions date range (default = today in the user's selected tz)
  const todayISO = fmtFileDate();
  const [txStartDate, setTxStartDate] = useState(todayISO);
  const [txEndDate, setTxEndDate] = useState(todayISO);

  // Transactions pagination
  const [txPage, setTxPage] = useState(1);
  const [txPageSize, setTxPageSize] = useState(10);

  // Cumulative report download
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  const BaseURL = "https://stagging-server.blueastercapital.com";

  // Selection
  const [selectedExchange, setSelectedExchange] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");

  // Data
  const [exchanges, setExchanges] = useState([]);
  const [allExchanges, setAllExchanges] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [openPositions, setOpenPositions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [balance, setBalance] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [transfers, setTransfers] = useState([]);

  const [isLoading, setIsLoading] = useState({
    exchanges: false,
    accounts: false,
    positions: false,
    transactions: false,
    balance: false,
    settlements: false,
    transfers: false
  });
  
  const token = sessionStorage.getItem('AdminAuthToken');

  // CSV Helpers
  const convertToCSV = (data, headers) => {
    const headerRow = headers.join(",");
    const dataRows = data.map((row) =>
      headers
        .map((header) => {
          const propertyMap = {
            'Instrument': 'instrument',
            'Side': 'side',
            'Leverage': 'leverage',
            'Amount': 'amount',
            'Value': 'value',
            'Avg Price': 'avgPrice',
            'Mark Price': 'markPrice',
            'Current Market Price': 'currentPrice',
            'UPL': 'upl',
            'IM': 'im',
            'MM': 'mm',
            'Type': 'type',
            'Position': 'position',
            'Price': 'price',
            'Funding': 'funding',
            'Fee Rate': 'feeRate',
            'Fee': 'fee',
            'Currency': 'currency',
            'Equity': 'equity',
            'Margin Balance': 'marginBalance',
            'Initial Margin': 'initialMargin',
            'Maintenance Margin': 'maintenanceMargin',
            'Available Balance': 'availableBalance',
            'Status': 'state',
            'Date': 'timestamp',
            'Position Size': 'positionSize',
            'Index Price': 'indexPrice',
            'MarkPrice': 'markPrice',
            'PNL': 'pnl',
            'Funding Fee': 'fundingFee',
            'Session PNL': 'sessionPnl',
            'Other Party': 'otherParty',
            'Direction': 'direction'
          };
          
          const propertyName = propertyMap[header] || header.toLowerCase().replace(/\s+/g, "");
          let value = row[propertyName] ?? "";
          // Format timestamp/date fields in the user's selected timezone so
          // the CSV matches what is shown on screen.
          if (propertyName === "timestamp" || propertyName === "date" || propertyName === "time") {
            value = fmtMaybeDate(value);
          }
          return `"${String(value).replace(/"/g, '""')}"`;
        })
        .join(",")
    );
    return [headerRow, ...dataRows].join("\n");
  };

  const downloadCSV = (csvData, filename) => {
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // PDF Generation
  const generatePDF = (data, headers, title, filename) => {
    const doc = new jsPDF('landscape');
    
    // Add logo on top right
    const logoUrl = '/assets/images/BlueAsterAR.png';
    doc.addImage(logoUrl, 'PNG', doc.internal.pageSize.width - 40, 10, 30, 10);
    
    // Add title
    doc.setFontSize(16);
    doc.text(title, 14, 15);
    
    // Add date and filters info
    doc.setFontSize(10);
    doc.text(`Generated on: ${fmtDate(Date.now())}`, 14, 22);
    if (selectedExchange) {
      doc.text(`Exchange: ${getExchangeName(selectedExchange)}`, 14, 28);
    }
    if (selectedAccount) {
      doc.text(`Account: ${selectedAccount}`, 14, 34);
    }
    
    // Prepare table data
    const tableData = data.map(item => {
      return headers.map(header => {
        const propertyMap = {
          'Instrument': 'instrument',
          'Side': 'side',
          'Leverage': 'leverage',
          'Amount': 'amount',
          'Value': 'value',
          'Avg Price': 'avgPrice',
          'Mark Price': 'markPrice',
          'Current Market Price': 'currentPrice',
          'UPL': 'upl',
          'IM': 'im',
          'MM': 'mm',
          'Type': 'type',
          'Position': 'position',
          'Price': 'price',
          'Funding': 'funding',
          'Fee Rate': 'feeRate',
          'Fee': 'fee',
          'Currency': 'currency',
          'Equity': 'equity',
          'Margin Balance': 'marginBalance',
          'Initial Margin': 'initialMargin',
          'Maintenance Margin': 'maintenanceMargin',
          'Available Balance': 'availableBalance',
          'Status': 'state',
          'Date': 'timestamp',
          'Position Size': 'positionSize',
          'Index Price': 'indexPrice',
          'MarkPrice': 'markPrice',
          'PNL': 'pnl',
          'Funding Fee': 'fundingFee',
          'Session PNL': 'sessionPnl',
          'Other Party': 'otherParty',
          'Direction': 'direction'
        };
        
        const propertyName = propertyMap[header] || header.toLowerCase().replace(/\s+/g, '');
        const raw = item[propertyName];
        if (raw === undefined || raw === null || raw === '') return '';
        // Format timestamp/date fields in the user's selected timezone.
        if (propertyName === 'timestamp' || propertyName === 'date' || propertyName === 'time') {
          return fmtMaybeDate(raw);
        }
        return raw;
      });
    });
    
    // Add table using autoTable
    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 40,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
      alternateRowStyles: { fillColor: [240, 240, 240] }
    });
    
    // Get the final Y position after the table
    const finalY = doc.lastAutoTable.finalY || 40;
    
    // Add disclaimer at the bottom
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont('italic');
    
    const disclaimerText = "Disclaimer: We do not guarantee the accuracy of this report as it is derived from the exchange directly. For any discrepancies pertaining to the transactions, balances, etc please contact the trading exchange.";
    
    const pageWidth = doc.internal.pageSize.width;
    const margin = 14;
    const maxWidth = pageWidth - (margin * 2);
    
    const splitText = doc.splitTextToSize(disclaimerText, maxWidth);
    doc.text(splitText, margin, finalY + 15);
    
    // Save PDF
    doc.save(filename);
  };

  // Fetch all exchanges
  useEffect(() => {
    const fetchAllExchanges = async () => {
      setIsLoading((prev) => ({ ...prev, exchanges: true }));
      try {
        const res = await axios.get(`${BaseURL}/api/exchanges`);
        setAllExchanges(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading((prev) => ({ ...prev, exchanges: false }));
      }
    };
    fetchAllExchanges();
  }, []);

  const getExchangeName = (id) => {
    const exchange = allExchanges.find((e) => e.exchange_id.toString() === id);
    return exchange ? exchange.exchange_name : "";
  };

  // Fetch user exchanges
  const fetchExchanges = async () => {
    if (!clientId ) return;
    setIsLoading((prev) => ({ ...prev, exchanges: true }));
    try {
      const response = await axios.get(`${BaseURL}/api/client-apis/${clientId}/exchanges`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const uniqueExchanges = Array.from(new Map(response.data.map((ex) => [ex.exchange_id, ex])).values());
      setExchanges(uniqueExchanges);
    } catch (error) {
      console.error("Error fetching exchanges:", error);
      toast.error((error.response?.data?.message) || "Failed to load exchanges");
    } finally {
      setIsLoading((prev) => ({ ...prev, exchanges: false }));
    }
  };

  // Fetch accounts
  const fetchAccounts = async (exchangeId) => {
    if (!clientId || !token) return;
    setIsLoading((prev) => ({ ...prev, accounts: true }));
    try {
      const res = await axios.get(`${BaseURL}/api/client-apis/${clientId}/exchange/${exchangeId}/accounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAccounts(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to load accounts");
    } finally {
      setIsLoading((prev) => ({ ...prev, accounts: false }));
    }
  };

  const fetchOpenPositions = async () => {
    if (!clientId || !token || !selectedExchange || !selectedAccount) return;
    setIsLoading((prev) => ({ ...prev, positions: true }));
    try {
      const res = await axios.get(
        `${BaseURL}/api/account-tracking/${clientId}/${selectedExchange}/${selectedAccount}/open-positions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOpenPositions(res.data["open-positions"] || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load open positions");
    } finally {
      setIsLoading((prev) => ({ ...prev, positions: false }));
    }
  };

  const parseTxTimestamp = (tx) => {
    const v = tx?.timestamp ?? tx?.date ?? tx?.time;
    if (v === undefined || v === null || v === "") return NaN;
    if (typeof v === "number") return v < 1e12 ? v * 1000 : v;
    const ms = new Date(v).getTime();
    return Number.isNaN(ms) ? NaN : ms;
  };

  const fetchTransactions = async (startDate = txStartDate, endDate = txEndDate) => {
    if (!clientId || !token || !selectedExchange || !selectedAccount) return;
    if (startDate && endDate && startDate > endDate) {
      toast.error("Start date cannot be after end date.");
      return;
    }
    setIsLoading((prev) => ({ ...prev, transactions: true }));
    try {
      const res = await axios.get(
        `${BaseURL}/api/account-tracking/${clientId}/${selectedExchange}/${selectedAccount}/todays-transactions`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { startDate, endDate },
        }
      );
      const raw = res.data["todays-transactions"] || res.data["transactions"] || [];

      // Client-side date filter (safety net — backend may ignore startDate/endDate params)
      let filtered = raw;
      if (startDate && endDate) {
        const startMs = new Date(`${startDate}T00:00:00`).getTime();
        const endMs = new Date(`${endDate}T23:59:59.999`).getTime();
        filtered = raw.filter((tx) => {
          const ms = parseTxTimestamp(tx);
          if (Number.isNaN(ms)) return false;
          return ms >= startMs && ms <= endMs;
        });
      }

      setTransactions(filtered);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load transactions");
    } finally {
      setIsLoading((prev) => ({ ...prev, transactions: false }));
    }
  };

  const applyTxDateRange = () => {
    setLoadedKeys((prev) => {
      const next = new Set(prev);
      next.delete("transactions");
      return next;
    });
    fetchTransactions(txStartDate, txEndDate).then(() => {
      setLoadedKeys((prev) => {
        const next = new Set(prev);
        next.add("transactions");
        return next;
      });
    });
  };

  const setTxRangePreset = (preset) => {
    const DAY_MS = 24 * 60 * 60 * 1000;
    const endMs = Date.now();
    let startMs = endMs;
    if (preset === "7d") startMs = endMs - 6 * DAY_MS;
    else if (preset === "30d") startMs = endMs - 29 * DAY_MS;
    // Format both edges in the user's selected display tz so "Today" means
    // today wherever the user has set their timezone, not browser-local.
    const s = fmtFileDate(startMs);
    const e = fmtFileDate(endMs);
    setTxStartDate(s);
    setTxEndDate(e);
    setLoadedKeys((prev) => {
      const next = new Set(prev);
      next.delete("transactions");
      return next;
    });
    fetchTransactions(s, e).then(() => {
      setLoadedKeys((prev) => {
        const next = new Set(prev);
        next.add("transactions");
        return next;
      });
    });
  };

  const fetchBalance = async () => {
    if (!clientId || !token || !selectedExchange || !selectedAccount) return;
    setIsLoading((prev) => ({ ...prev, balance: true }));
    try {
      const res = await axios.get(
        `${BaseURL}/api/account-tracking/${clientId}/${selectedExchange}/${selectedAccount}/balance`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBalance(res.data["balance"] || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load balance");
    } finally {
      setIsLoading((prev) => ({ ...prev, balance: false }));
    }
  };

  const fetchSettlements = async () => {
    if (!clientId || !token || !selectedExchange || !selectedAccount) return;
    
    setIsLoading(prev => ({...prev, settlements: true}))
    try {
      const response = await axios.get(`${BaseURL}/api/account-tracking/${clientId}/${selectedExchange}/${selectedAccount}/settlements`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      setSettlements(response.data.settlements || []);
    } catch (error) {
      console.error("Error fetching settlements:", error)
      toast.error(error.response?.data?.message || "Failed to load settlements")
    } finally {
      setIsLoading(prev => ({...prev, settlements: false}))
    }
  }

  const fetchTransfers = async () => {
    if (!clientId || !token || !selectedExchange || !selectedAccount) return
    
    setIsLoading(prev => ({...prev, transfers: true}))
    try {
      const response = await axios.get(`${BaseURL}/api/account-tracking/${clientId}/${selectedExchange}/${selectedAccount}/fund-transfers`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      setTransfers(response.data["fund-transfers"] || []);
    } catch (error) {
      console.error("Error fetching transfers:", error)
      toast.error(error.response?.data?.message || "Failed to load transfers")
    } finally {
      setIsLoading(prev => ({...prev, transfers: false}))
    }
  }

  // ===== Cumulative Report (all exchanges × all accounts) =====
  const REPORT_SECTIONS = [
    {
      title: "Open Positions",
      endpoint: "open-positions",
      responseKey: "open-positions",
      headers: ["Instrument", "Side", "Leverage", "Amount", "Value", "Avg Price", "Mark Price", "Current Market Price", "UPL", "IM", "MM"],
      fields: ["instrument", "side", "leverage", "amount", "value", "avgPrice", "markPrice", "currentPrice", "upl", "im", "mm"],
    },
    {
      title: "Transactions",
      endpoint: "todays-transactions",
      responseKey: "todays-transactions",
      headers: ["Instrument", "Type", "Side", "Amount", "Position", "Price", "Funding", "Fee Rate", "Fee", "Date"],
      fields: ["instrument", "type", "side", "amount", "position", "price", "funding", "feeRate", "fee", "date"],
    },
    {
      title: "Account Balance",
      endpoint: "balance",
      responseKey: "balance",
      headers: ["Type", "Currency", "Equity", "Margin Balance", "Initial Margin", "Maintenance Margin", "Available Balance"],
      fields: ["type", "currency", "equity", "marginBalance", "initialMargin", "maintenanceMargin", "availableBalance"],
    },
    {
      title: "Settlement History",
      endpoint: "settlements",
      responseKey: "settlements",
      headers: ["Instrument", "Position Size", "Index Price", "Mark Price", "PNL", "Funding Fee", "Session PNL", "Date"],
      fields: ["instrument", "positionSize", "indexPrice", "markPrice", "pnl", "fundingFee", "sessionPnl", "timestamp"],
    },
    {
      title: "Fund Transfers",
      endpoint: "fund-transfers",
      responseKey: "fund-transfers",
      headers: ["Currency", "Amount", "Direction", "Other Party", "Status", "Date"],
      fields: ["currency", "amount", "direction", "otherParty", "state", "timestamp"],
    },
  ];

  const handleDownloadCumulativeReport = async () => {
    if (!clientId || !token) {
      toast.error("Authentication required.");
      return;
    }
    setIsDownloadingReport(true);
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    try {
      const exRes = await axios.get(`${BaseURL}/api/client-apis/${clientId}/exchanges`, authHeaders);
      const uniqueExchanges = Array.from(
        new Map((exRes.data || []).map((ex) => [ex.exchange_id, ex])).values()
      );

      if (uniqueExchanges.length === 0) {
        toast.warn("No exchanges found for this client.");
        return;
      }

      const aggregated = [];
      for (const ex of uniqueExchanges) {
        const exchangeId = ex.exchange_id;
        const exchangeName = getExchangeName(exchangeId.toString()) || ex.exchange_name || `Exchange ${exchangeId}`;

        let accs = [];
        try {
          const r = await axios.get(`${BaseURL}/api/client-apis/${clientId}/exchange/${exchangeId}/accounts`, authHeaders);
          accs = r.data || [];
        } catch (e) { /* skip */ }

        if (accs.length === 0) {
          aggregated.push({ exchangeName, accountId: null, sections: [] });
          continue;
        }

        for (const acc of accs) {
          const accountId = acc.name || acc.account_id || acc.account || acc.id;
          if (!accountId) continue;

          const sections = await Promise.all(
            REPORT_SECTIONS.map(async (sec) => {
              try {
                const r = await axios.get(
                  `${BaseURL}/api/account-tracking/${clientId}/${exchangeId}/${accountId}/${sec.endpoint}`,
                  authHeaders
                );
                return { ...sec, rows: r.data?.[sec.responseKey] || [] };
              } catch (e) {
                return { ...sec, rows: [] };
              }
            })
          );
          aggregated.push({ exchangeName, accountId, sections });
        }
      }

      generateCumulativePDF(aggregated);
      toast.success("Report downloaded.");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to generate report.");
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const generateCumulativePDF = (aggregated) => {
    const doc = new jsPDF("landscape");
    const pageWidth = doc.internal.pageSize.width;
    const margin = 14;

    try {
      doc.addImage("/assets/images/BlueAsterAR.png", "PNG", pageWidth - 50, 10, 36, 12);
    } catch (e) { /* logo optional */ }

    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42);
    doc.text("Cumulative Client Report", margin, 18);

    doc.setFontSize(10);
    doc.setTextColor(71, 85, 105);
    doc.text(`Client: ${clientTitle || "—"}`, margin, 26);
    doc.text(`Username: ${clientEmail || "—"}`, margin, 32);
    doc.text(`Generated on: ${fmtDate(Date.now())}`, margin, 38);

    let cursorY = 46;

    // Drop sections with no rows; drop groups that end up with nothing to show
    const nonEmpty = aggregated
      .map((g) => ({
        ...g,
        sections: (g.sections || []).filter((s) => Array.isArray(s.rows) && s.rows.length > 0),
      }))
      .filter((g) => g.sections.length > 0);

    if (nonEmpty.length === 0) {
      doc.setFontSize(11);
      doc.setTextColor(100, 100, 100);
      doc.text("No data available for this client.", margin, cursorY);
    }

    nonEmpty.forEach((group) => {
      if (cursorY > doc.internal.pageSize.height - 40) {
        doc.addPage();
        cursorY = 20;
      }

      doc.setFontSize(13);
      doc.setTextColor(29, 78, 216);
      doc.text(`${group.exchangeName} • Account: ${group.accountId}`, margin, cursorY);
      cursorY += 6;

      group.sections.forEach((sec) => {
        doc.setFontSize(11);
        doc.setTextColor(15, 23, 42);
        doc.text(sec.title, margin, cursorY);
        cursorY += 2;

        const body = sec.rows.map((row) =>
          sec.fields.map((f) => {
            let v = row?.[f];
            if (f === "currentPrice" && (v === undefined || v === null || v === "")) {
              v = row?.lastPrice ?? row?.currentMarketPrice ?? row?.indexPrice;
            }
            if (f === "upl" && (v === undefined || v === null || v === "")) {
              v = row?.ups ?? row?.uspl;
            }
            if (v === undefined || v === null) return "N/A";
            // Render epoch/ISO timestamps in the user's selected display tz.
            if (f === "timestamp" || f === "date" || f === "time") {
              return fmtDate(v);
            }
            const str = String(v);
            if (str.trim() === "") return "N/A";
            if (f === "type") return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
            if (f === "leverage") return `${str}x`;
            return str;
          })
        );

        autoTable(doc, {
          head: [sec.headers],
          body,
          startY: cursorY + 2,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [37, 99, 235], textColor: 255 },
          alternateRowStyles: { fillColor: [240, 245, 255] },
          margin: { left: margin, right: margin },
          didParseCell: (data) => {
            if (data.section !== "body") return;
            const fieldName = sec.fields?.[data.column.index];
            const isPnlField = ["upl", "pnl", "sessionPnl"].includes(fieldName);
            const raw = String(data.cell.text?.[0] ?? "").replace("%", "").trim();
            if (raw === "N/A" || raw === "") {
              data.cell.styles.textColor = [148, 163, 184];
              data.cell.styles.fontStyle = "italic";
              return;
            }
            if (!isPnlField) return;
            const n = Number(raw);
            if (Number.isNaN(n)) return;
            if (n > 0) {
              data.cell.styles.textColor = [22, 163, 74];
              data.cell.styles.fontStyle = "bold";
            } else if (n < 0) {
              data.cell.styles.textColor = [220, 38, 38];
              data.cell.styles.fontStyle = "bold";
            }
          },
        });

        cursorY = (doc.lastAutoTable?.finalY || cursorY) + 8;
      });

      cursorY += 4;
    });

    const totalPages = doc.internal.getNumberOfPages();
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.setFont("helvetica", "italic");
    const disclaimer =
      "Disclaimer: We do not guarantee the accuracy of this report as it is derived from the exchange directly. For any discrepancies pertaining to the transactions, balances, etc please contact the trading exchange.";
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      const ph = doc.internal.pageSize.height;
      const split = doc.splitTextToSize(disclaimer, pageWidth - margin * 2);
      doc.text(split, margin, ph - 10);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 25, ph - 4);
    }

    const safeName = (clientTitle || "client").toString().replace(/[^a-z0-9_-]/gi, "_");
    doc.save(`cumulative_report_${safeName}_${fmtFileDate()}.pdf`);
  };

  // CSV download handlers
  const normalizePositions = (rows) =>
    (rows || []).map((p) => ({
      ...p,
      currentPrice: p.currentPrice ?? p.lastPrice ?? p.currentMarketPrice ?? p.indexPrice ?? "",
      upl: p.upl ?? p.ups ?? p.uspl ?? "",
    }));

  const downloadOpenPositionsCSV = () => {
    const headers = ["Instrument", "Side", "Leverage", "Amount", "Value", "Avg Price", "Mark Price", "Current Market Price", "UPL", "IM", "MM"];
    const csvData = convertToCSV(normalizePositions(openPositions), headers);
    downloadCSV(csvData, `open_positions_${selectedExchange}_${selectedAccount}_${fmtFileDate()}.csv`);
  };

  const downloadTransactionsCSV = () => {
    const headers = ["Instrument", "Type", "Side", "Amount", "Position", "Price", "Funding", "Fee Rate", "Fee"];
    const csvData = convertToCSV(transactions, headers);
    downloadCSV(csvData, `transactions_${selectedExchange}_${selectedAccount}_${fmtFileDate()}.csv`);
  };

  const downloadBalanceCSV = () => {
    const headers = ["Type","Currency", "Equity", "Margin Balance", "Initial Margin", "Maintenance Margin", "Available Balance"];
    const csvData = convertToCSV(balance, headers);
    downloadCSV(csvData, `balance_${selectedExchange}_${selectedAccount}_${fmtFileDate()}.csv`);
  };

  const downloadSettlementsCSV = () => {
    const headers = ["Instrument", "Position Size", "Index Price", "Mark Price", "PNL", "Funding Fee", "Session PNL", "Date"];
    const csvData = convertToCSV(settlements, headers);
    downloadCSV(csvData, `settlements_${selectedExchange}_${selectedAccount}_${fmtFileDate()}.csv`);
  };

  const downloadTransfersCSV = () => {
    const headers = ["Currency", "Amount", "Direction", "Other Party", "Status", "Date"];
    const csvData = convertToCSV(transfers, headers);
    downloadCSV(csvData, `transfers_${selectedExchange}_${selectedAccount}_${fmtFileDate()}.csv`);
  };

  // PDF download handlers
  const downloadOpenPositionsPDF = () => {
    const headers = ["Instrument", "Side", "Leverage", "Amount", "Value", "Avg Price", "Mark Price", "Current Market Price", "UPL", "IM", "MM"];
    generatePDF(normalizePositions(openPositions), headers, "Open Positions", `open_positions_${selectedExchange}_${selectedAccount}_${fmtFileDate()}.pdf`);
  };

  const downloadTransactionsPDF = () => {
    const headers = ["Instrument", "Type", "Side", "Amount", "Position", "Price", "Funding", "Fee Rate", "Fee"];
    generatePDF(transactions, headers, "Transactions", `transactions_${selectedExchange}_${selectedAccount}_${fmtFileDate()}.pdf`);
  };

  const downloadBalancePDF = () => {
    const headers = ["Type","Currency", "Equity", "Margin Balance", "Initial Margin", "Maintenance Margin", "Available Balance"];
    generatePDF(balance, headers, "Account Balance", `balance_${selectedExchange}_${selectedAccount}_${fmtFileDate()}.pdf`);
  };

  const downloadSettlementsPDF = () => {
    const headers = ["Instrument", "Position Size", "Index Price", "Mark Price", "PNL", "Funding Fee", "Session PNL", "Date"];
    generatePDF(settlements, headers, "Settlement History", `settlements_${selectedExchange}_${selectedAccount}_${fmtFileDate()}.pdf`);
  };

  const downloadTransfersPDF = () => {
    const headers = ["Currency", "Amount", "Direction", "Other Party", "Status", "Date"];
    generatePDF(transfers, headers, "Fund Transfers", `transfers_${selectedExchange}_${selectedAccount}_${fmtFileDate()}.pdf`);
  };

  useEffect(() => {
    if (allExchanges.length > 0) {
      const table = $("#dataTable").DataTable({ destroy: true });
      return () => {
        table.destroy();
      };
    }
  }, [allExchanges]);

  // Auth validation using clientId
  useEffect(() => {
    fetchExchanges();
  }, [clientId]);

  // Resolve client title/email if not provided via router state (e.g., page refresh)
  useEffect(() => {
    if (!clientId || (clientTitle && clientEmail)) return;
    const resolveClient = async () => {
      try {
        const res = await axios.get(`${BaseURL}/api/clients`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const found = (res.data || []).find(
          (c) => String(c.client_id) === String(clientId)
        );
        if (found) {
          if (!clientTitle) setClientTitle(found.client_title || "");
          if (!clientEmail) setClientEmail(found.email || "");
        }
      } catch (e) {
        // silent — header just shows the id
      }
    };
    resolveClient();
  }, [clientId]);

  const na = (v) => {
    if (v === null || v === undefined) return "N/A";
    if (typeof v === "number" && Number.isNaN(v)) return "N/A";
    if (typeof v === "string" && v.trim() === "") return "N/A";
    return v;
  };

  // Render numeric values with red (negative) / green (positive) / gray italic (N/A)
  const numCell = (v, suffix = "") => {
    if (v === null || v === undefined) return <span className="bac-na">N/A</span>;
    const str = String(v).trim();
    if (str === "") return <span className="bac-na">N/A</span>;
    const num = Number(str);
    if (Number.isNaN(num)) return <span>{str}{suffix}</span>;
    const cls = num > 0 ? "bac-pos" : num < 0 ? "bac-neg" : "";
    return <span className={cls}>{str}{suffix}</span>;
  };

  const initialsFor = (name = "") => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const palette = ["#2563eb", "#0ea5e9", "#7c3aed", "#db2777", "#16a34a", "#f59e0b", "#ef4444", "#0891b2"];
  const colorFor = (key = "") => {
    let hash = 0;
    for (let i = 0; i < key.length; i++) hash = key.charCodeAt(i) + ((hash << 5) - hash);
    return palette[Math.abs(hash) % palette.length];
  };

  const selectedExchangeName = selectedExchange ? getExchangeName(selectedExchange) : "";
  const accent = colorFor(clientTitle || String(clientId || ""));

  // Fetch accounts when exchange changes
  useEffect(() => {
    if (selectedExchange) {
      setSelectedAccount("");
      fetchAccounts(selectedExchange);
    }
  }, [selectedExchange]);

  // Auto-select first account
  useEffect(() => {
    if (accounts.length > 0) {
      setSelectedAccount(accounts[0].name);
    }
  }, [accounts]);

  // Active tab + lazy-load cache (which tabs have been fetched for the current selection)
  const [activeKey, setActiveKey] = useState("open-positions");
  const [loadedKeys, setLoadedKeys] = useState(() => new Set());

  // Reset cached data + loaded flags whenever the selection changes
  useEffect(() => {
    setOpenPositions([]);
    setTransactions([]);
    setBalance([]);
    setSettlements([]);
    setTransfers([]);
    setLoadedKeys(new Set());
    setTxPage(1);
  }, [selectedExchange, selectedAccount]);

  // Whenever transactions change, snap pagination back to first page
  useEffect(() => {
    setTxPage(1);
  }, [transactions, txPageSize]);

  // Lazy-load: fetch only the active tab's data, and only once per selection
  useEffect(() => {
    if (!selectedExchange || !selectedAccount) return;
    if (loadedKeys.has(activeKey)) return;

    const run = async () => {
      switch (activeKey) {
        case "open-positions": await fetchOpenPositions(); break;
        case "transactions":   await fetchTransactions();   break;
        case "balance":        await fetchBalance();        break;
        case "settlement":     await fetchSettlements();    break;
        case "transfer-funds": await fetchTransfers();      break;
        default: return;
      }
      setLoadedKeys((prev) => {
        const next = new Set(prev);
        next.add(activeKey);
        return next;
      });
    };
    run();
  }, [activeKey, selectedExchange, selectedAccount, loadedKeys]);

  return (
    <div className="portfolio-container bac-portfolio">
      {/* Client Hero */}
      <div className="bac-hero">
        <div className="bac-hero-bg" />
        <div className="bac-hero-inner">
          <div className="bac-hero-toolbar">
            <button className="bac-back-btn" onClick={() => navigate("/clients")} title="Back to Clients">
              <Icon icon="solar:arrow-left-linear" />
              <span>Back to Clients</span>
            </button>
            <button
              className="bac-report-btn"
              onClick={handleDownloadCumulativeReport}
              disabled={isDownloadingReport}
              title="Download Cumulative Report"
            >
              {isDownloadingReport ? (
                <>
                  <span className="bac-report-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Icon icon="solar:document-add-bold-duotone" />
                  <span>Download Report</span>
                </>
              )}
            </button>
          </div>

          <div className="bac-hero-main">
            <div className="bac-hero-left">
              <span className="bac-hero-avatar" style={{ background: `${accent}` }}>
                {initialsFor(clientTitle || "?")}
              </span>
              <div className="bac-hero-text">
                <span className="bac-hero-eyebrow">
                  <Icon icon="solar:pie-chart-2-bold-duotone" />
                  Portfolio Overview
                </span>
                <h2>{clientTitle || "Client"}</h2>
                {clientEmail && (
                  <p>
                    <Icon icon="solar:user-id-bold-duotone" />
                    {clientEmail}
                  </p>
                )}
              </div>
            </div>

            <div className="bac-hero-meta">
              <div className="bac-meta-chip">
                <span className="bac-meta-label">Exchange</span>
                <span className="bac-meta-value">
                  {selectedExchangeName || <em>Not selected</em>}
                </span>
              </div>
              <div className="bac-meta-chip">
                <span className="bac-meta-label">Account</span>
                <span className="bac-meta-value">
                  {selectedAccount || <em>Not selected</em>}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Card className="portfolio-card">
        <Card.Header className="portfolio-card-header">
          <h5>
            <Icon icon="solar:filter-bold-duotone" style={{ marginRight: 8, verticalAlign: "-3px" }} />
            Select Exchange & Account
          </h5>
        </Card.Header>
        <Card.Body>
          <Form>
            <div className="form-row">
              <Form.Group className="mb-3 form-group-custom">
                <Form.Label>Select Exchange</Form.Label>
                <div className="select-wrapper">
                  <Form.Select 
                    value={selectedExchange} 
                    onChange={(e) => setSelectedExchange(e.target.value)} 
                    disabled={isLoading.exchanges}
                    className="custom-select"
                  >
                    <option>{isLoading.exchanges ? "Loading..." : "Choose exchange..."}</option>
                    {exchanges.map((ex) => (
                      <option key={ex.exchange_id} value={ex.exchange_id.toString()}>
                        {getExchangeName(ex.exchange_id.toString())}
                      </option>
                    ))}
                  </Form.Select>
                  {isLoading.exchanges && <div className="select-spinner"><Spinner animation="border" size="sm" /></div>}
                </div>
              </Form.Group>

              <Form.Group className="mb-3 form-group-custom">
                <Form.Label>Select Account</Form.Label>
                <div className="select-wrapper">
                  <Form.Select 
                    value={selectedAccount} 
                    onChange={(e) => setSelectedAccount(e.target.value)} 
                    disabled={isLoading.accounts || !selectedExchange}
                    className="custom-select"
                  >
                    <option>
                      {isLoading.accounts
                        ? "Loading..."
                        : !selectedExchange
                        ? "Select exchange first"
                        : accounts.length === 0
                        ? "No accounts available"
                        : "Choose account..."}
                    </option>
                    {accounts.map((acc) => (
                      <option key={acc.name} value={acc.name}>
                        {acc.name}
                      </option>
                    ))}
                  </Form.Select>
                  {isLoading.accounts && <div className="select-spinner"><Spinner animation="border" size="sm" /></div>}
                </div>
              </Form.Group>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Tabs */}
      <div className="custom-tabs-container">
        <Tabs activeKey={activeKey} onSelect={(k) => setActiveKey(k)} id="portfolio-tabs" className="custom-tabs">
          <Tab eventKey="open-positions" title={
            <span className="tab-title">
              {isLoading.positions ? <Spinner animation="border" size="sm" /> : <i className="fas fa-chart-line"></i>}
              Open Positions
            </span>
          }>
            <Card className="tab-content-card">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5>Open Positions</h5>
                <div className="d-flex gap-2">
                  <Button variant="outline-primary" size="sm" onClick={downloadOpenPositionsCSV} disabled={openPositions.length === 0}>
                    <Download className="h-4 w-4 mr-2" /> CSV
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={downloadOpenPositionsPDF} disabled={openPositions.length === 0}>
                    <FileText className="h-4 w-4 mr-2" /> PDF
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {isLoading.positions ? (
                  <div className="loader-container">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p>Loading positions data...</p>
                  </div>
                ) : !selectedExchange || !selectedAccount ? (
                  <div className="no-data-message">
                    <i className="fas fa-hand-pointer"></i>
                    <p>Please select an exchange and account to view open positions</p>
                  </div>
                ) : openPositions.length === 0 ? (
                  <div className="no-data-message">
                    <i className="fas fa-inbox"></i>
                    <p>No open positions found</p>
                  </div>
                ) : (
                  <div className="table-responsive custom-table-container">
                    <Table hover className="custom-table" id="dataTable">
                      <thead className="table-header">
                        <tr>
                          <th>Instrument</th>
                          <th>Side</th>
                          <th>Leverage</th>
                          <th>Amount</th>
                          <th>Value</th>
                          <th>Avg Price</th>
                          <th>Mark Price</th>
                          <th>Current Market Price</th>
                          <th>UPL</th>
                          <th>IM</th>
                          <th>MM</th>
                        </tr>
                      </thead>
                      <tbody>
                        {openPositions.map((pos, idx) => {
                          const leverage = pos.leverage;
                          const currentPrice = pos.currentPrice ?? pos.lastPrice ?? pos.currentMarketPrice ?? pos.indexPrice;
                          return (
                            <tr key={idx}>
                              <td>{na(pos.instrument)}</td>
                              <td>
                                {pos.side ? (
                                  <span
                                    className={`side-badge ${
                                      pos.side?.toLowerCase() === 'long' || pos.side?.toLowerCase() === 'buy'
                                        ? 'long'
                                        : 'short'
                                    }`}
                                  >
                                    {pos.side}
                                  </span>
                                ) : "N/A"}
                              </td>
                              <td>{leverage !== undefined && leverage !== null && leverage !== "" ? `${leverage}x` : "N/A"}</td>
                              <td>{na(pos.amount)}</td>
                              <td>{na(pos.value)}</td>
                              <td>{na(pos.avgPrice)}</td>
                              <td>{na(pos.markPrice)}</td>
                              <td>{na(currentPrice)}</td>
                              <td>
                                {numCell(pos.upl ?? pos.ups ?? pos.uspl)}
                              </td>
                              <td>{na(pos.im)}</td>
                              <td>{na(pos.mm)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="transactions" title={
            <span className="tab-title">
              {isLoading.transactions ? <Spinner animation="border" size="sm" /> : <i className="fas fa-exchange-alt"></i>}
              Transactions
            </span>
          }>
            <Card className="tab-content-card">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5>Transactions</h5>
                <div className="d-flex gap-2">
                  <Button variant="outline-primary" size="sm" onClick={downloadTransactionsCSV} disabled={transactions.length === 0}>
                    <Download className="h-4 w-4 mr-2" /> CSV
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={downloadTransactionsPDF} disabled={transactions.length === 0}>
                    <FileText className="h-4 w-4 mr-2" /> PDF
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <div className="bac-tx-filter">
                  <div className="bac-tx-dates">
                    <label className="bac-tx-field">
                      <span>From</span>
                      <AppDatePicker
                        value={txStartDate}
                        onChange={setTxStartDate}
                        max={txEndDate || todayISO}
                        placeholder="From"
                        ariaLabel="Transaction start date"
                      />
                    </label>
                    <label className="bac-tx-field">
                      <span>To</span>
                      <AppDatePicker
                        value={txEndDate}
                        onChange={setTxEndDate}
                        min={txStartDate}
                        max={todayISO}
                        placeholder="To"
                        ariaLabel="Transaction end date"
                      />
                    </label>
                    <Button
                      size="sm"
                      onClick={applyTxDateRange}
                      disabled={!selectedExchange || !selectedAccount || isLoading.transactions}
                      className="bac-tx-apply"
                    >
                      <Icon icon="solar:magnifer-bold" /> Apply
                    </Button>
                  </div>
                  <div className="bac-tx-presets">
                    <button type="button" className="bac-preset" onClick={() => setTxRangePreset("today")} disabled={isLoading.transactions}>Today</button>
                    <button type="button" className="bac-preset" onClick={() => setTxRangePreset("7d")} disabled={isLoading.transactions}>Last 7 Days</button>
                    <button type="button" className="bac-preset" onClick={() => setTxRangePreset("30d")} disabled={isLoading.transactions}>Last 30 Days</button>
                  </div>
                </div>

                {isLoading.transactions ? (
                  <div className="loader-container">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p>Loading transactions data...</p>
                  </div>
                ) : !selectedExchange || !selectedAccount ? (
                  <div className="no-data-message">
                    <i className="fas fa-hand-pointer"></i>
                    <p>Please select an exchange and account to view transactions</p>
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="no-data-message">
                    <i className="fas fa-inbox"></i>
                    <p>No transactions found in the selected date range</p>
                  </div>
                ) : (
                  (() => {
                    const total = transactions.length;
                    const totalPages = Math.max(1, Math.ceil(total / txPageSize));
                    const currentPage = Math.min(txPage, totalPages);
                    const startIdx = (currentPage - 1) * txPageSize;
                    const endIdx = Math.min(startIdx + txPageSize, total);
                    const pageRows = transactions.slice(startIdx, endIdx);

                    const buildPageNumbers = () => {
                      const pages = [];
                      const window = 1;
                      const add = (p) => pages.push(p);
                      const addEllipsis = () => pages.push("…");
                      add(1);
                      if (currentPage - window > 2) addEllipsis();
                      for (let p = Math.max(2, currentPage - window); p <= Math.min(totalPages - 1, currentPage + window); p++) add(p);
                      if (currentPage + window < totalPages - 1) addEllipsis();
                      if (totalPages > 1) add(totalPages);
                      return pages;
                    };

                    return (
                      <>
                        <div className="table-responsive custom-table-container">
                          <Table hover className="custom-table">
                            <thead className="table-header">
                              <tr>
                                <th>Instrument</th>
                                <th>Type</th>
                                <th>Side</th>
                                <th>Amount</th>
                                <th>Position</th>
                                <th>Price</th>
                                <th>Fee Charged</th>
                                <th>Date</th>
                                <th>Time</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pageRows.map((tx, idx) => (
                                <tr key={startIdx + idx}>
                                  <td>{na(tx.instrument)}</td>
                                  <td>{tx.type ? tx.type.charAt(0).toUpperCase() + tx.type.slice(1).toLowerCase() : "N/A"}</td>
                                  <td>
                                    {tx.side ? (
                                      <span className={`side-badge ${tx.side?.toLowerCase() === 'buy' ? 'long' : 'short'}`}>
                                        {tx.side}
                                      </span>
                                    ) : "N/A"}
                                  </td>
                                  <td>{na(tx.amount)}</td>
                                  <td>{na(tx.position)}</td>
                                  <td>{na(tx.price)}</td>
                                  <td>{na(tx.fee)}</td>
                                  <td>{fmtDateOnly(tx.timestamp ?? tx.date ?? tx.time)}</td>
                                  <td>{fmtTimeOnly(tx.timestamp ?? tx.date ?? tx.time)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>

                        <div className="bac-pagination">
                          <div className="bac-pagi-left">
                            <span>Rows per page</span>
                            <select
                              value={txPageSize}
                              onChange={(e) => setTxPageSize(Number(e.target.value))}
                            >
                              {[10, 25, 50, 100].map((n) => (
                                <option key={n} value={n}>{n}</option>
                              ))}
                            </select>
                            <span className="bac-pagi-info">
                              Showing <strong>{total === 0 ? 0 : startIdx + 1}</strong>–<strong>{endIdx}</strong> of <strong>{total}</strong>
                            </span>
                          </div>

                          <div className="bac-pagi-right">
                            <button
                              className="bac-pagi-btn"
                              onClick={() => setTxPage(1)}
                              disabled={currentPage === 1}
                              title="First page"
                            >
                              <Icon icon="solar:double-alt-arrow-left-linear" />
                            </button>
                            <button
                              className="bac-pagi-btn"
                              onClick={() => setTxPage((p) => Math.max(1, p - 1))}
                              disabled={currentPage === 1}
                              title="Previous"
                            >
                              <Icon icon="solar:alt-arrow-left-linear" />
                            </button>

                            {buildPageNumbers().map((p, i) =>
                              p === "…" ? (
                                <span key={`e-${i}`} className="bac-pagi-ellipsis">…</span>
                              ) : (
                                <button
                                  key={p}
                                  className={`bac-pagi-btn ${currentPage === p ? "is-active" : ""}`}
                                  onClick={() => setTxPage(p)}
                                >
                                  {p}
                                </button>
                              )
                            )}

                            <button
                              className="bac-pagi-btn"
                              onClick={() => setTxPage((p) => Math.min(totalPages, p + 1))}
                              disabled={currentPage === totalPages}
                              title="Next"
                            >
                              <Icon icon="solar:alt-arrow-right-linear" />
                            </button>
                            <button
                              className="bac-pagi-btn"
                              onClick={() => setTxPage(totalPages)}
                              disabled={currentPage === totalPages}
                              title="Last page"
                            >
                              <Icon icon="solar:double-alt-arrow-right-linear" />
                            </button>
                          </div>
                        </div>
                      </>
                    );
                  })()
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="balance" title={
            <span className="tab-title">
              {isLoading.balance ? <Spinner animation="border" size="sm" /> : <i className="fas fa-wallet"></i>}
              Balance
            </span>
          }>
            <Card className="tab-content-card">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5>Account Balance</h5>
                <div className="d-flex gap-2">
                  <Button variant="outline-primary" size="sm" onClick={downloadBalanceCSV} disabled={balance.length === 0}>
                    <Download className="h-4 w-4 mr-2" /> CSV
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={downloadBalancePDF} disabled={balance.length === 0}>
                    <FileText className="h-4 w-4 mr-2" /> PDF
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {isLoading.balance ? (
                  <div className="loader-container">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p>Loading balance data...</p>
                  </div>
                ) : !selectedExchange || !selectedAccount ? (
                  <div className="no-data-message">
                    <i className="fas fa-hand-pointer"></i>
                    <p>Please select an exchange and account to view balance</p>
                  </div>
                ) : balance.length === 0 ? (
                  <div className="no-data-message">
                    <i className="fas fa-inbox"></i>
                    <p>No balance information available</p>
                  </div>
                ) : (
                  <div className="table-responsive custom-table-container">
                    <Table hover className="custom-table" id="dataTable">
                      <thead className="table-header">
                        <tr>
                          <th>Type</th>
                          <th>Currency</th>
                          <th>Equity</th>
                          <th>Margin Balance</th>
                          <th>Initial Margin</th>
                          <th>Maintenance Margin</th>
                          <th>Available Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {balance.map((b, idx) => (
                          <tr key={idx}>
                            <td>
                              {b.type ? (
                                <span className="currency-badge">
                                  {b.type.charAt(0).toUpperCase() + b.type.slice(1).toLowerCase()}
                                </span>
                              ) : "N/A"}
                            </td>
                            <td>
                              {b.currency ? (
                                <span className="currency-badge">{b.currency}</span>
                              ) : "N/A"}
                            </td>
                            <td>{na(b.equity)}</td>
                            <td>{na(b.marginBalance)}</td>
                            <td>{na(b.initialMargin)}</td>
                            <td>{na(b.maintenanceMargin)}</td>
                            <td className="available-balance">{na(b.availableBalance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="settlement" title={
            <span className="tab-title">
              {isLoading.settlements ? <Spinner animation="border" size="sm" /> : <CreditCard className="h-4 w-4" />}
              Settlement
            </span>
          }>
            <Card className="tab-content-card">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5>Settlement History</h5>
                {settlements.length > 0 && (
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" size="sm" onClick={downloadSettlementsCSV}>
                      <Download className="h-4 w-4 mr-2" /> CSV
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={downloadSettlementsPDF}>
                      <FileText className="h-4 w-4 mr-2" /> PDF
                    </Button>
                  </div>
                )}
              </Card.Header>
              <Card.Body>
                {isLoading.settlements ? (
                  <div className="loader-container">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p>Loading settlements data...</p>
                  </div>
                ) : !selectedExchange || !selectedAccount ? (
                  <div className="no-data-message">
                    <i className="fas fa-inbox"></i>
                    <p>Please select an exchange and account to view settlements</p>
                  </div>
                ) : settlements.length === 0 ? (
                  <div className="no-data-message">
                    <i className="fas fa-inbox"></i>
                    <p>No settlement history found</p>
                  </div>
                ) : (
                  <div className="table-responsive custom-table-container">
                    <Table hover className="custom-table" id="settlements-table">
                      <thead className="table-header">
                        <tr>
                          <th>Instrument</th>
                          <th>Position Size</th>
                          <th>Index Price</th>
                          <th>Mark Price</th>
                          <th>PNL</th>
                          <th>Funding Fee</th>
                          <th>Session PNL</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {settlements.map((settlement, index) => (
                          <tr key={index}>
                            <td className="font-medium">{na(settlement.symbol)}</td>
                            <td>{na(settlement.positionSize)}</td>
                            <td>{typeof settlement.indexPrice === "number" ? settlement.indexPrice.toFixed(2) : "N/A"}</td>
                            <td>{typeof settlement.markPrice === "number" ? settlement.markPrice.toFixed(2) : "N/A"}</td>
                            <td>
                              {numCell(typeof settlement.pnl === "number" ? (settlement.pnl * 100).toFixed(6) : settlement.pnl, "%")}
                            </td>
                            <td>{typeof settlement.fundingFee === "number" ? (settlement.fundingFee * 100).toFixed(6) + "%" : "N/A"}</td>
                            <td>
                              {numCell(typeof settlement.sessionPnl === "number" ? settlement.sessionPnl.toFixed(2) : settlement.sessionPnl)}
                            </td>
                            <td>{fmtDateOnly(settlement.timestamp ?? settlement.date ?? settlement.time)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="transfer-funds" title={
            <span className="tab-title">
              {isLoading.transfers ? <Spinner animation="border" size="sm" /> : <ArrowLeftRight className="h-4 w-4" />}
              Transfer Funds
            </span>
          }>
            <Card className="tab-content-card">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5>Fund Transfers</h5>
                {transfers.length > 0 && (
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" size="sm" onClick={downloadTransfersCSV}>
                      <Download className="h-4 w-4 mr-2" /> CSV
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={downloadTransfersPDF}>
                      <FileText className="h-4 w-4 mr-2" /> PDF
                    </Button>
                  </div>
                )}
              </Card.Header>
              <Card.Body>
                {isLoading.transfers ? (
                  <div className="loader-container">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p>Loading transfers data...</p>
                  </div>
                ) : !selectedExchange || !selectedAccount ? (
                  <div className="no-data-message">
                    <i className="fas fa-inbox"></i>
                    <p>Please select an exchange and account to view transfers</p>
                  </div>
                ) : transfers.length === 0 ? (
                  <div className="no-data-message">
                    <i className="fas fa-inbox"></i>
                    <p>No fund transfers found</p>
                  </div>
                ) : (
                  <div className="table-responsive custom-table-container">
                    <Table hover className="custom-table" id="transfers-table">
                      <thead className="table-header">
                        <tr>
                          <th>Currency</th>
                          <th>Amount</th>
                          <th>Direction</th>
                          <th>Other Party</th>
                          <th>Status</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transfers.map((transfer, index) => (
                          <tr key={index}>
                            <td>
                              {transfer.currency ? (
                                <Badge variant="outline" className="border-warning text-warning font-bold">
                                  {transfer.currency}
                                </Badge>
                              ) : "N/A"}
                            </td>
                            <td className="font-medium">{typeof transfer.amount === "number" ? transfer.amount.toLocaleString() : "N/A"}</td>
                            <td>
                              {transfer.direction ? (
                                <Badge variant={transfer.direction === "income" || transfer.direction === "IN" ? "success" : "danger"}>
                                  {transfer.direction === "income" || transfer.direction === "IN" ? "Incoming" : "Outgoing"}
                                </Badge>
                              ) : "N/A"}
                            </td>
                            <td>{na(transfer.otherParty)}</td>
                            <td>{na(transfer.state)}</td>
                            <td>{fmtDate(transfer.timestamp ?? transfer.date ?? transfer.time)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </div>

      <style>{`
        .bac-portfolio { padding-top: 0; }

        /* ===== Client Hero ===== */
        .bac-hero {
          position: relative;
          border-radius: 20px;
          overflow: hidden;
          margin-bottom: 18px;
          color: #fff;
          box-shadow: 0 14px 40px rgba(13, 59, 142, 0.25);
        }
        .bac-hero-bg {
          position: absolute; inset: 0;
          background:
            radial-gradient(700px 300px at 0% 0%, rgba(255,255,255,0.18), transparent 60%),
            radial-gradient(500px 280px at 100% 100%, rgba(34,211,238,0.30), transparent 60%),
            linear-gradient(135deg, #0d3b8e 0%, #1d4ed8 50%, #0ea5e9 100%);
        }
        .bac-hero-bg::before {
          content: "";
          position: absolute; inset: 0;
          background-image: radial-gradient(rgba(255,255,255,0.18) 1px, transparent 1px);
          background-size: 22px 22px;
          opacity: .25;
        }
        .bac-hero-inner {
          position: relative;
          padding: 22px 26px 24px;
        }

        .bac-hero-toolbar {
          display: flex; align-items: center; justify-content: space-between;
          gap: 10px; flex-wrap: wrap;
        }
        .bac-back-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,0.12);
          color: #fff;
          font-size: 13px;
          font-weight: 500;
          border: 1px solid rgba(255,255,255,0.22);
          cursor: pointer;
          transition: all .2s ease;
          backdrop-filter: blur(4px);
        }
        .bac-back-btn:hover {
          background: rgba(255,255,255,0.22);
          transform: translateX(-2px);
        }
        .bac-back-btn svg { font-size: 16px; }

        .bac-report-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 9px 16px;
          border-radius: 999px;
          background: #fff;
          color: #1d4ed8;
          font-size: 13px;
          font-weight: 700;
          border: 1px solid rgba(255,255,255,0.4);
          cursor: pointer;
          transition: all .2s ease;
          box-shadow: 0 6px 16px rgba(0,0,0,0.15);
        }
        .bac-report-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 10px 22px rgba(0,0,0,0.22);
          background: #f0f7ff;
        }
        .bac-report-btn:disabled { opacity: .8; cursor: not-allowed; }
        .bac-report-btn svg { font-size: 18px; }
        .bac-report-spin {
          width: 14px; height: 14px;
          border: 2px solid rgba(29, 78, 216, 0.25);
          border-left-color: #1d4ed8;
          border-radius: 50%;
          animation: bac-rspin .8s linear infinite;
          display: inline-block;
        }
        @keyframes bac-rspin { to { transform: rotate(360deg); } }

        .bac-hero-main {
          display: flex; align-items: center; justify-content: space-between;
          gap: 24px; flex-wrap: wrap;
          margin-top: 16px;
        }
        .bac-hero-left { display: flex; align-items: center; gap: 18px; min-width: 0; }
        .bac-hero-avatar {
          width: 72px; height: 72px;
          flex-shrink: 0;
          border-radius: 20px;
          display: inline-flex; align-items: center; justify-content: center;
          color: #fff;
          font-weight: 700;
          font-size: 24px;
          letter-spacing: 0.5px;
          box-shadow: 0 10px 24px rgba(0,0,0,0.18), inset 0 0 0 3px rgba(255,255,255,0.25);
        }
        .bac-hero-text { min-width: 0; }
        .bac-hero-eyebrow {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 12px;
          font-weight: 600;
          color: rgba(255,255,255,0.88);
          background: rgba(255,255,255,0.14);
          padding: 4px 10px;
          border-radius: 999px;
          margin-bottom: 8px;
        }
        .bac-hero-eyebrow svg { font-size: 14px; }
        .bac-hero-text h2 {
          margin: 0;
          font-size: 26px;
          font-weight: 700;
          letter-spacing: -0.4px;
          color: #fff;
          word-break: break-word;
        }
        .bac-hero-text p {
          margin: 6px 0 0;
          font-size: 13.5px;
          color: rgba(255,255,255,0.85);
          display: inline-flex; align-items: center; gap: 6px;
        }
        .bac-hero-text p svg { font-size: 16px; }

        .bac-hero-meta {
          display: flex; gap: 10px; flex-wrap: wrap;
        }
        .bac-meta-chip {
          background: rgba(255,255,255,0.10);
          border: 1px solid rgba(255,255,255,0.20);
          border-radius: 14px;
          padding: 10px 14px;
          min-width: 140px;
          display: flex; flex-direction: column;
          backdrop-filter: blur(4px);
        }
        .bac-meta-label {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.7px;
          color: rgba(255,255,255,0.7);
        }
        .bac-meta-value {
          font-size: 14px;
          font-weight: 600;
          color: #fff;
          margin-top: 2px;
          word-break: break-word;
        }
        .bac-meta-value em { color: rgba(255,255,255,0.55); font-weight: 400; font-style: normal; }

        /* ===== Selection card ===== */
        .bac-portfolio .portfolio-card {
          border: 1px solid #eef2f7;
          border-radius: 18px !important;
          box-shadow: 0 6px 24px rgba(15, 23, 42, 0.05);
          overflow: hidden;
        }
        .bac-portfolio .portfolio-card-header {
          background: linear-gradient(180deg, #fafbff 0%, #ffffff 100%) !important;
          color: #0f172a !important;
          border-bottom: 1px solid #eef2f7 !important;
          padding: 16px 22px !important;
        }
        .bac-portfolio .portfolio-card-header h5 {
          color: #0f172a !important;
          font-weight: 700;
          font-size: 16px;
          margin: 0;
        }
        .bac-portfolio .portfolio-card .card-body { padding: 20px 22px 6px; }
        .bac-portfolio .custom-select {
          height: 46px;
          border: 1.5px solid #e2e8f0 !important;
          border-radius: 12px !important;
          background-color: #f8fafc !important;
          font-size: 14px;
          transition: all .2s ease;
        }
        .bac-portfolio .custom-select:focus {
          border-color: #2563eb !important;
          background-color: #fff !important;
          box-shadow: 0 0 0 4px rgba(37,99,235,0.12) !important;
        }
        .bac-portfolio .form-label { font-weight: 600; color: #334155; font-size: 13px; }

        /* ===== Tabs ===== */
        .bac-portfolio .custom-tabs-container { margin-top: 18px; }
        .bac-portfolio .custom-tabs {
          background: #fff;
          padding: 6px 8px 0;
          border-radius: 16px 16px 0 0;
          border: 1px solid #eef2f7;
          border-bottom: none;
        }
        .bac-portfolio .custom-tabs .nav-link {
          color: #475569;
          font-weight: 600;
          font-size: 13.5px;
          border: none !important;
          padding: 12px 16px !important;
          border-radius: 10px !important;
          margin: 4px 4px 0 !important;
          transition: all .2s ease;
        }
        .bac-portfolio .custom-tabs .nav-link:hover { background: #eff6ff; color: #1d4ed8; }
        .bac-portfolio .custom-tabs .nav-link.active {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 60%, #0ea5e9 100%) !important;
          color: #fff !important;
          box-shadow: 0 6px 14px rgba(37,99,235,0.30);
        }
        .bac-portfolio .tab-content-card {
          border: 1px solid #eef2f7 !important;
          border-radius: 0 0 16px 16px !important;
          border-top: none !important;
          box-shadow: 0 6px 24px rgba(15, 23, 42, 0.05);
        }
        .bac-portfolio .tab-content-card .card-header {
          background: #f8fbff !important;
          border-bottom: 1px solid #eef2f7 !important;
          padding: 14px 22px !important;
        }
        .bac-portfolio .tab-content-card .card-header h5 {
          font-weight: 700; color: #0f172a; font-size: 15px; margin: 0;
        }

        /* ===== Transactions date filter ===== */
        .bac-tx-filter {
          display: flex; align-items: center; justify-content: space-between;
          gap: 14px; flex-wrap: wrap;
          padding: 14px 16px;
          margin-bottom: 14px;
          background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
          border: 1px solid #eef2f7;
          border-radius: 12px;
        }
        .bac-tx-dates { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; }
        .bac-tx-field { display: flex; flex-direction: column; gap: 4px; font-size: 12.5px; color: #475569; font-weight: 600; }
        .bac-tx-field input[type="date"] {
          height: 38px;
          border: 1.5px solid #e2e8f0;
          border-radius: 10px;
          padding: 0 10px;
          background: #fff;
          color: #0f172a;
          font-size: 13.5px;
          outline: none;
          transition: all .2s ease;
          min-width: 150px;
        }
        .bac-tx-field input[type="date"]:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 4px rgba(37,99,235,0.12);
        }
        .bac-tx-apply {
          height: 38px;
          padding: 0 16px !important;
          border: none !important;
          border-radius: 10px !important;
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 60%, #0ea5e9 100%) !important;
          color: #fff !important;
          font-weight: 600 !important;
          display: inline-flex !important; align-items: center; gap: 6px;
          box-shadow: 0 8px 16px rgba(37,99,235,0.25);
        }
        .bac-tx-apply:hover { transform: translateY(-1px); box-shadow: 0 10px 20px rgba(37,99,235,0.32); }
        .bac-tx-apply svg { font-size: 16px; }

        .bac-tx-presets { display: flex; gap: 6px; flex-wrap: wrap; }
        .bac-preset {
          height: 32px;
          padding: 0 12px;
          border-radius: 999px;
          font-size: 12.5px;
          font-weight: 600;
          color: #1d4ed8;
          background: #eff6ff;
          border: 1px solid #dbeafe;
          cursor: pointer;
          transition: all .15s ease;
        }
        .bac-preset:hover:not(:disabled) { background: #dbeafe; border-color: #93c5fd; }
        .bac-preset:disabled { opacity: .6; cursor: not-allowed; }

        /* ===== Pagination ===== */
        .bac-pagination {
          display: flex; align-items: center; justify-content: space-between;
          gap: 14px; flex-wrap: wrap;
          padding: 14px 4px 4px;
          margin-top: 8px;
          border-top: 1px solid #f1f5f9;
        }
        .bac-pagi-left { display: flex; align-items: center; gap: 10px; font-size: 13px; color: #475569; flex-wrap: wrap; }
        .bac-pagi-left select {
          height: 34px;
          padding: 0 28px 0 10px;
          border: 1.5px solid #e2e8f0;
          border-radius: 8px;
          background: #fff;
          color: #0f172a;
          font-size: 13px;
          outline: none;
        }
        .bac-pagi-left select:focus { border-color: #2563eb; }
        .bac-pagi-info strong { color: #0f172a; font-weight: 700; }

        .bac-pagi-right { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
        .bac-pagi-btn {
          min-width: 34px; height: 34px;
          padding: 0 10px;
          border: 1px solid #e2e8f0;
          background: #fff;
          color: #334155;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex; align-items: center; justify-content: center;
          transition: all .15s ease;
        }
        .bac-pagi-btn:hover:not(:disabled):not(.is-active) {
          background: #eff6ff;
          border-color: #bfdbfe;
          color: #1d4ed8;
        }
        .bac-pagi-btn.is-active {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          border-color: transparent;
          color: #fff;
          box-shadow: 0 6px 14px rgba(37, 99, 235, 0.25);
        }
        .bac-pagi-btn:disabled { opacity: .45; cursor: not-allowed; }
        .bac-pagi-btn svg { font-size: 18px; }
        .bac-pagi-ellipsis { padding: 0 6px; color: #94a3b8; }

        /* ===== Number cell coloring ===== */
        .bac-pos { color: #16a34a; font-weight: 600; }
        .bac-neg { color: #dc2626; font-weight: 600; }
        .bac-na  { color: #94a3b8; font-style: italic; }

        /* ===== Responsive ===== */
        @media (max-width: 768px) {
          .bac-tx-filter { padding: 12px; }
          .bac-tx-dates { width: 100%; }
          .bac-tx-field, .bac-tx-field input[type="date"] { width: 100%; min-width: 0; }
          .bac-tx-apply { width: 100%; justify-content: center; }
          .bac-hero-inner { padding: 18px; }
          .bac-hero-text h2 { font-size: 22px; }
          .bac-hero-avatar { width: 60px; height: 60px; font-size: 20px; border-radius: 16px; }
          .bac-hero-main { flex-direction: column; align-items: flex-start; }
          .bac-hero-meta { width: 100%; }
          .bac-meta-chip { flex: 1; min-width: 0; }
        }
        @media (max-width: 480px) {
          .bac-hero-text h2 { font-size: 19px; }
          .bac-meta-chip { flex: 1 1 100%; }
        }
      `}</style>
    </div>
  );
};

export default PortfolioDetails;